/**
 * LoginPage - Página de login do sistema
 */
import { useEffect, useState } from 'react'
import { useAuth } from '@context/AuthContext'
import ThemeToggle from '@components/shared/ThemeToggle'
import { INTERNAL_BRAND } from '@config/brand'

export default function LoginPage() {
  const { login } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    document.body.classList.add('login-page-active')

    return () => document.body.classList.remove('login-page-active')
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (!email.trim() || !password.trim()) {
      setError('Por favor, preencha todos os campos')
      return
    }

    setLoading(true)
    try {
      const result = await login(email.trim(), password.trim())
      if (result.success) {
        setSuccess(true)
      } else {
        setError(result.error)
      }
    } catch {
      setError('Erro ao tentar fazer login')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="login-page">
      <div className="login-technical-grid" aria-hidden="true" />

      <section className="login-story" aria-labelledby="login-story-title">
        <div className="login-product-art login-product-art--harmonia" aria-hidden="true">
          <img src={INTERNAL_BRAND.artworkPath} alt="" />
        </div>
        <p className="login-eyebrow"><span /> Inteligência aplicada</p>
        <h1 id="login-story-title">
          Clareza para transformar prazos em <em>direção segura.</em>
        </h1>
        <p className="login-story-lead">
          Operação jurídica, conhecimento e dados conectados em um fluxo único para decisões mais precisas.
        </p>

        <div className="login-route" aria-label="Sinal, direção e resultado">
          <span>Sinal</span><i /><span>Direção</span><i /><span>Resultado</span>
        </div>

        <dl className="login-pillars">
          <div>
            <dt>01</dt>
            <dd><strong>Operação</strong><span>Prazos e responsáveis visíveis.</span></dd>
          </div>
          <div>
            <dt>02</dt>
            <dd><strong>Contexto</strong><span>Informação jurídica organizada.</span></dd>
          </div>
          <div>
            <dt>03</dt>
            <dd><strong>Evidência</strong><span>Decisões apoiadas por dados.</span></dd>
          </div>
        </dl>
      </section>

        <div className="login-container">
        <div className="login-panel-toolbar">
          <ThemeToggle variant="login" />
        </div>
        <div className="login-header">
          <h2>{INTERNAL_BRAND.stem}<span>{INTERNAL_BRAND.accent}</span></h2>
          <p>{INTERNAL_BRAND.tagline}</p>
        </div>

        <form className="login-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email"><span>01</span> Email profissional</label>
            <input
              type="email"
              id="email"
              className="form-input"
              placeholder="seu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              inputMode="email"
              maxLength={120}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password"><span>02</span> Senha de acesso</label>
            <div className="password-container">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                className="form-input"
                placeholder="Digite sua senha"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                maxLength={128}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              >
                <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
              </button>
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <button
            type="submit"
            className={`btn btn-primary ${success ? 'btn-success' : ''}`}
            disabled={loading || success}
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin" /> Entrando...
              </>
            ) : success ? (
              <>
                <i className="fas fa-check" /> Sucesso!
              </>
            ) : (
              <>
                <i className="fas fa-sign-in-alt" /> Entrar no Sistema
              </>
            )}
          </button>
        </form>

        <p className="login-security-note">
          <i className="fas fa-shield-alt" aria-hidden="true" /> Ambiente seguro e monitorado
        </p>
      </div>
    </div>
  )
}
